import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { normaliseOrderServices } from '@mypoz/shared';
import { useLocale } from '@mypoz/i18n';

import { OrderServicesContext, type OrderServicesState } from './orderServicesContext.js';
import { fetchOrderServiceRows } from './catalog-api.js';

/**
 * `order_services` (28 warehouse add-on services, grouped under 11 warehouse keys — MYP-53 slice
 * 2), fetched once for the whole app, the same tier as `WarehousesProvider`/`PackagesProvider`.
 *
 * Split out from `OrderCatalogProvider` rather than folded into it: the Jira ticket is explicit
 * that create-order reference data gets "its own provider mounted around the create/edit routes,
 * so 205 channels and 534 cities are not fetched for someone who only opens the Orders list" —
 * but `OrderDetailPanel`, which renders on that Orders list, also needs to resolve a service id to
 * its name, and it has no route of its own to wrap. `order_services` is 28 rows, not 205 or 534,
 * so giving it its own small app-wide provider satisfies both: the Orders list gets the service
 * names it needs without paying for channels or the destination cascade, and `OrderCatalogProvider`
 * stays scoped to the two routes the ticket names.
 */
export function OrderServicesProvider({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  const language = locale === 'zh' ? 'zh' : 'en';
  const [rows, setRows] = useState<
    readonly unknown[] | Readonly<Record<string, unknown>> | undefined
  >(undefined);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let current = true;

    setRows(undefined);
    setFailed(false);

    fetchOrderServiceRows().then(
      (next) => {
        if (current) setRows(next);
      },
      (cause: unknown) => {
        if (!current) return;
        console.error('[orders] could not load order services', cause);
        setFailed(true);
      },
    );

    return () => {
      current = false;
    };
  }, [attempt]);

  const reload = useCallback(() => setAttempt((previous) => previous + 1), []);

  const value = useMemo<OrderServicesState>(() => {
    const services = rows ? normaliseOrderServices(rows, language) : [];

    return {
      services,
      status: rows ? 'ready' : failed ? 'error' : 'loading',
      reload,
    };
  }, [rows, failed, language, reload]);

  return <OrderServicesContext.Provider value={value}>{children}</OrderServicesContext.Provider>;
}
